import type { TFunction } from 'i18next';
import { type GraphQLErrorLike, getGraphQLErrorMessage } from './graphQLError';

const SNACKBAR_VARIANTS = [
  'default',
  'info',
  'success',
  'warning',
  'error',
] as const;

export type GraphQLErrorToastVariant = (typeof SNACKBAR_VARIANTS)[number];

export type GraphQLErrorToast = {
  message: string;
  variant: GraphQLErrorToastVariant;
  logout?: boolean;
};

const toToastVariant = (
  value: string | undefined,
): GraphQLErrorToastVariant => {
  if (
    value != null &&
    (SNACKBAR_VARIANTS as readonly string[]).includes(value)
  ) {
    return value as GraphQLErrorToastVariant;
  }
  return 'error';
};

const isOrganizationUserNotFound = (gqlError: GraphQLErrorLike) => {
  if (gqlError.extensions?.code !== 'NOT_ACCESSIBLE') return false;
  const readableData = gqlError.extensions?.readableData;
  return (
    readableData?.errorCode === 'ORG_USER_DOES_NOT_EXIST' ||
    gqlError.message === 'OrganizationUser does not exist' ||
    readableData?.message === 'OrganizationUser does not exist'
  );
};

export const buildNetworkErrorToast = (
  networkError: { statusCode?: number },
  t: TFunction,
): GraphQLErrorToast => {
  if (networkError.statusCode === 401) {
    return {
      message: t('shared:validationError.sessionTimeout'),
      variant: 'error',
    };
  }

  return {
    message: t('shared:somethingWentWrong'),
    variant: 'error',
  };
};

export const buildGraphQLErrorToast = (
  translateBackendString: (translatedObject: unknown) => string,
  gqlError: GraphQLErrorLike,
  t: TFunction,
  fallbackMessage?: string,
): GraphQLErrorToast | undefined => {
  const readableData = gqlError.extensions?.readableData;
  const userMessage = translateBackendString(readableData?.userMessage);
  const uniqueFields = (readableData?.fields ?? []).filter(
    (field) => Object.hasOwn(field, 'kind') && field.kind === 'unique',
  );
  const validationErrorWithFields = `${t('shared:validationError.unique')} ${uniqueFields
    .map((field) => field.path)
    .join(', ')
    .replace(/, ([^,]*)$/, ' or $1')}.`;

  if (
    gqlError.extensions?.code === 'VALIDATION' &&
    readableData?.code === 11000
  ) {
    return {
      message:
        userMessage ||
        `${t('shared:validationError.unique')} ${Object.keys(readableData?.keyValue ?? {})[0]}.`,
      variant: 'error',
    };
  }

  if (gqlError.extensions?.code === 'VALIDATION' && uniqueFields.length > 0) {
    return {
      message: userMessage || validationErrorWithFields,
      variant: 'error',
    };
  }

  if (gqlError.extensions?.code === 'TOKEN_REFRESH_REQUIRED') {
    return {
      message: userMessage || t('shared:validationError.tokenRefreshFailed'),
      variant: 'error',
      logout: true,
    };
  }

  if (gqlError.extensions?.code === 11000) {
    return {
      message: t('shared:validationError.duplicateRecord'),
      variant: 'error',
    };
  }

  if (isOrganizationUserNotFound(gqlError)) {
    return {
      message: t('shared:backendIssuedError.organizationUserNotFound'),
      variant: 'error',
    };
  }

  const message = getGraphQLErrorMessage(
    translateBackendString,
    gqlError,
    fallbackMessage,
  );
  if (!message) return undefined;

  return {
    message,
    variant: toToastVariant(readableData?.userMessageSeverity),
  };
};
